import { HttpClient } from '@angular/common/http';
import { defer, map, Observable, throwIfEmpty, timeout } from 'rxjs';
import { casesCaseIdAuditGet } from './generated/fn/cases/cases-case-id-audit-get';
import { casesCaseIdGet } from './generated/fn/cases/cases-case-id-get';
import { casesCaseIdStepsStepIdClaimPost } from './generated/fn/cases/cases-case-id-steps-step-id-claim-post';
import { casesCaseIdStepsStepIdDecidePost } from './generated/fn/cases/cases-case-id-steps-step-id-decide-post';
import { casesCaseIdStepsStepIdFormGet } from './generated/fn/cases/cases-case-id-steps-step-id-form-get';
import { casesCaseIdStepsStepIdReleasePost } from './generated/fn/cases/cases-case-id-steps-step-id-release-post';
import { casesCaseIdWorkflowGet } from './generated/fn/cases/cases-case-id-workflow-get';
import { casesQueueGet, CasesQueueGet$Params } from './generated/fn/cases/cases-queue-get';
import { caseTypesCaseTypeWorkflowGet } from './generated/fn/cases/case-types-case-type-workflow-get';
import { meRolesGet } from './generated/fn/cases/me-roles-get';
import { AuditTrailResponse } from './generated/models/audit-trail-response';
import { CaseStateView } from './generated/models/case-state-view';
import { ClaimResponse } from './generated/models/claim-response';
import { DecideHttpRequest } from './generated/models/decide-http-request';
import { DecideResponse } from './generated/models/decide-response';
import { MeRolesResponse } from './generated/models/me-roles-response';
import { QueueResponse } from './generated/models/queue-response';
import { ReleaseHttpRequest } from './generated/models/release-http-request';
import { ReleaseResponse } from './generated/models/release-response';
import { StepForm } from './generated/models/step-form';
import { WorkflowGraph } from './generated/models/workflow-graph';
import { WorkflowEngineGateway } from './workflow-engine-gateway';

/** The longest an engine read may take, token acquisition included. */
export const ENGINE_READ_TIMEOUT_MS = 20_000;

/**
 * Makes a read end in a value or an error. When Entra refuses the engine token, MSAL's redirect fallback
 * completes the request without a response or never settles it, which would leave every caller loading.
 */
function settled<T>(read: Observable<T>): Observable<T> {
  return read.pipe(throwIfEmpty(), timeout(ENGINE_READ_TIMEOUT_MS));
}

export class HttpWorkflowEngineGateway implements WorkflowEngineGateway {
  constructor(
    private readonly _http: HttpClient,
    private readonly _apiUrl: string,
    private readonly _configured = true,
  ) {}

  private _request<T>(send: () => Observable<T>): Observable<T> {
    return defer(() => {
      if (!this._configured) {
        throw new Error('Workflow engine URL and delegated scope must be configured.');
      }
      return send();
    });
  }

  getMyRoles(caseType?: string): Observable<MeRolesResponse> {
    return this._request(() => settled(
      meRolesGet(this._http, this._apiUrl, caseType ? { caseType } : undefined).pipe(map(response => response.body)),
    ));
  }

  getQueue(params?: CasesQueueGet$Params): Observable<QueueResponse> {
    return this._request(() => settled(casesQueueGet(this._http, this._apiUrl, params).pipe(map(response => response.body))));
  }

  getCaseTypeWorkflow(caseType: string): Observable<WorkflowGraph> {
    return this._request(() => settled(
      caseTypesCaseTypeWorkflowGet(this._http, this._apiUrl, { caseType }).pipe(map(response => response.body)),
    ));
  }

  getCaseState(caseId: string): Observable<CaseStateView> {
    return this._request(() => settled(casesCaseIdGet(this._http, this._apiUrl, { caseId }).pipe(map(response => response.body))));
  }

  getCaseWorkflow(caseId: string): Observable<WorkflowGraph> {
    return this._request(() => settled(casesCaseIdWorkflowGet(this._http, this._apiUrl, { caseId }).pipe(map(response => response.body))));
  }

  getAuditTrail(caseId: string): Observable<AuditTrailResponse> {
    return this._request(() => settled(casesCaseIdAuditGet(this._http, this._apiUrl, { caseId }).pipe(map(response => response.body))));
  }

  getStepForm(caseId: string, stepId: string): Observable<StepForm> {
    return this._request(() => settled(
      casesCaseIdStepsStepIdFormGet(this._http, this._apiUrl, { caseId, stepId }).pipe(map(response => response.body)),
    ));
  }

  claimStep(caseId: string, stepId: string): Observable<ClaimResponse> {
    return this._request(() => casesCaseIdStepsStepIdClaimPost(this._http, this._apiUrl, { caseId, stepId }).pipe(
      map(response => response.body),
    ));
  }

  decideStep(caseId: string, stepId: string, body: DecideHttpRequest): Observable<DecideResponse> {
    return this._request(() => casesCaseIdStepsStepIdDecidePost(this._http, this._apiUrl, { caseId, stepId, body }).pipe(
      map(response => response.body),
    ));
  }

  releaseStep(caseId: string, stepId: string, body: ReleaseHttpRequest): Observable<ReleaseResponse> {
    return this._request(() => casesCaseIdStepsStepIdReleasePost(this._http, this._apiUrl, { caseId, stepId, body }).pipe(
      map(response => response.body),
    ));
  }
}